import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../hooks/useConfirm';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Icon from '../../components/AppIcon';
import RequestFormFields from './components/RequestFormFields';
import PrintableForm from './components/PrintableForm';
import UploadSignedDocument from './components/UploadSignedDocument';
import { supplierRequestService, getErrorMessage } from '../../services/apiService';
import { classificationSummary, CLASSIFICATION_FIELDS } from '../../utils/classification';
import { EMPTY_FORM, formFromDraft, validateRequestForm, needsClassification, CLASSIFICATION_FIRST } from './requestForm';

const SupplierRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [draftRequests, setDraftRequests] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [showUploadView, setShowUploadView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch user's draft requests on mount
  useEffect(() => {
    fetchDraftRequests();
  }, []);

  // Handle URL parameters for direct navigation (continue draft or upload)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const draftId = params.get('draftId');
    const showUpload = params.get('showUpload');

    if (draftId) {
      loadDraftById(parseInt(draftId), showUpload === 'true');
    }
  }, [location.search]);

  const loadDraftById = async (draftId, showUploadView = false) => {
    try {
      const result = await supplierRequestService.getRequestById(draftId);
      const draft = result.data;
      if (showUploadView) {
        openUpload(draft);
      } else {
        setCurrentDraftId(draft.id);
        setFormData(formFromDraft(draft));
      }
      
      // Clear URL parameters
      navigate('/supplier-request-form', { replace: true });
    } catch (err) {
      console.error('Error loading draft:', err);
      setError('Failed to load draft request');
    }
  };

  const fetchDraftRequests = async () => {
    try {
      const result = await supplierRequestService.getSupplierRequests({ status: 'DRAFT' });
      setDraftRequests(result.data || []);
    } catch (err) {
      console.error('Error fetching draft requests:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = validateRequestForm(formData);

    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setError('Please fix the errors in the form');
      return false;
    }

    setFieldErrors({});
    setError(null);
    return true;
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError(null);

    try {
      let result;
      if (currentDraftId) {
        result = await supplierRequestService.updateDraft(currentDraftId, formData);
      } else {
        result = await supplierRequestService.createDraft(formData);
      }
      
      const savedDraft = result.data;
      setCurrentDraftId(savedDraft.id);
      setSuccessMessage('Draft saved successfully!');
      fetchDraftRequests();
      
      setTimeout(() => setSuccessMessage(''), 3000);
      return true;
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to save draft');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPrint = async () => {
    if (!validateForm()) {
      return;
    }

    // The printed form is what the plant head signs: only print what was really saved.
    if (await handleSaveDraft()) {
      setShowPrintView(true);
    }
  };

  // The signed document completes a request; one saved before the form asked what the supplier
  // is has to answer that first (it cannot be submitted without it).
  const openUpload = (draft) => {
    setCurrentDraftId(draft.id);
    setFormData(formFromDraft(draft));
    if (needsClassification(draft)) {
      setShowUploadView(false);
      setError(CLASSIFICATION_FIRST);
      setFieldErrors(Object.fromEntries(CLASSIFICATION_FIELDS.filter((f) => !draft[f]).map((f) => [f, 'Required'])));
      return;
    }
    setError(null);
    setShowUploadView(true);
  };

  const handlePrintComplete = () => {
    setShowPrintView(false);
    setShowUploadView(true);
  };

  const handleUploadSuccess = () => {
    setSuccessMessage('Request submitted successfully!');
    setTimeout(() => {
      navigate('/supplier-requests');
    }, 2000);
  };

  const handleLoadDraft = (draft) => {
    setFormData(formFromDraft(draft));
    setFieldErrors({});
    setError(null);
    setCurrentDraftId(draft.id);
    setSuccessMessage('Draft loaded successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteDraft = async (draftId) => {
    const confirmed = await confirm({
      title: 'Delete Draft',
      message: 'Are you sure you want to delete this draft?',
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    try {
      await supplierRequestService.deleteRequest(draftId);

      setSuccessMessage('Draft deleted successfully!');
      fetchDraftRequests();
      
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
        setFormData(EMPTY_FORM);
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to delete draft');
    }
  };

  const handleNewForm = () => {
    setCurrentDraftId(null);
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setError(null);
    setShowUploadView(false);
  };

  if (showPrintView) {
    return <PrintableForm formData={formData} onBack={() => setShowPrintView(false)} onPrintComplete={handlePrintComplete} />;
  }

  if (showUploadView && currentDraftId) {
    return (
      <UploadSignedDocument
        requestId={currentDraftId}
        onBack={() => setShowUploadView(false)}
        onSuccess={handleUploadSuccess}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb 
        items={[
          { label: 'Dashboard', path: '/dashboard-overview' },
          { label: 'Supplier Request Form', path: '/supplier-request-form' }
        ]} 
      />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Supplier Request Form
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Request creation of a new vendor code
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Card className="border border-emerald-200 shadow-sm bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-emerald-600" />
              <span className="text-emerald-800">{successMessage}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="border border-red-200 shadow-sm bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Draft Requests Section */}
      {draftRequests.length > 0 && (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Your Draft Requests</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleNewForm}
                iconName="Plus"
                iconPosition="left"
              >
                New Form
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {draftRequests.map(draft => (
                <Card key={draft.id} className="border border-slate-200 hover:shadow-md transition-all duration-200">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">{draft.required_vendor_name || 'Untitled Draft'}</h3>
                        <p className="text-sm text-slate-600 mb-1">
                          Created: {new Date(draft.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-slate-600 mb-1" data-testid={`draft-classification-${draft.id}`}>
                          {needsClassification(draft) ? 'Classification not chosen yet' : classificationSummary(draft)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Icon name="Info" size={12} />
                          <span>
                            {needsClassification(draft)
                              ? "Choose the supplier classification, then print and upload the signed form"
                              : draft.plant_head_approval_document
                                ? "Ready to submit - document uploaded"
                                : "Waiting for signed document upload"}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleLoadDraft(draft)}
                          iconName="Edit"
                          iconPosition="left"
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="default"
                          size="sm" 
                          onClick={() => openUpload(draft)}
                          iconName="Upload"
                          iconPosition="left"
                        >
                          Upload Document
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteDraft(draft.id)}
                          iconName="Trash2"
                          className="text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardHeader>
          <CardTitle>Form Details</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestFormFields formData={formData} onChange={handleInputChange} errors={fieldErrors} />

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={loading}
              className="flex-1"
              iconName="Save"
              iconPosition="left"
            >
              Save as Draft
            </Button>
            <Button
              variant="default"
              onClick={handleSaveAndPrint}
              disabled={loading}
              className="flex-1"
              iconName="Printer"
              iconPosition="left"
            >
              Save & Print
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default SupplierRequestForm;

